'use client'

import { ChevronsLeftIcon, ChevronsRightIcon } from 'lucide-react'
import { type ReactNode, useState } from 'react'

import { cn } from '@/lib/cn'
import type { Option } from '@/types/option'
import { Button } from '../button'
import { Skeleton } from '../skeleton'

type TransferListProps = {
  itemsToAdd: Option[]
  itemsAdded: Option[]
  onChange: (updatedItems: Option[]) => void
  error?: boolean
  disabled?: boolean
  leftLabel?: ReactNode
  rightLabel?: ReactNode
  isLoading?: boolean
}

type ListProps = {
  items: Option[]
  selectedItems: string[]
  error: boolean
  disabled: boolean
  isLoading: boolean
  handleSelect: (value: string) => void
}

const List = ({
  error,
  disabled,
  isLoading,
  items,
  selectedItems,
  handleSelect,
}: ListProps) => {
  return (
    <div
      className={cn(
        'rounded-sm bg-background',
        disabled ? 'cursor-not-allowed' : '',
      )}
    >
      <ul
        className={cn(
          'flex max-h-48 w-full min-w-80 flex-col gap-3 overflow-y-scroll rounded-sm border p-4 shadow-sm',
          error ? 'border-system-state-error' : 'border-system-border-primary',
        )}
      >
        {isLoading ? (
          <>
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
          </>
        ) : (
          items.map((item) => (
            <li
              key={item.value}
              className={cn(
                'flex w-full items-center gap-1.5 rounded-md px-3 py-2 text-sm text-system-text-primary hover:bg-muted',
                selectedItems.includes(item.value)
                  ? 'bg-system-background-grey'
                  : '',
                disabled ? 'cursor-not-allowed opacity-50' : '',
              )}
              onClick={() => handleSelect(item.value)}
            >
              {item.label}
            </li>
          ))
        )}
      </ul>
    </div>
  )
}

const TransferList = ({
  itemsAdded,
  itemsToAdd,
  onChange,
  error = false,
  disabled = false,
  leftLabel,
  rightLabel,
  isLoading = false,
}: TransferListProps) => {
  const [selectedItems, setSelectedItems] = useState<string[]>([])
  const [focusedItem, setFocusedItem] = useState<string | null>(null)

  const handleSelect = (value: string, isAdded: boolean) => {
    if (disabled || isLoading) return

    if (focusedItem === value) {
      handleTransfer([value], isAdded)
      return
    }

    setFocusedItem(value)
    setSelectedItems((prevState) => [...prevState, value])
  }

  const handleTransfer = (values: string[], isAdded: boolean) => {
    if (disabled || isLoading) return

    const newItems = isAdded
      ? itemsAdded.filter((item) => !values.includes(item.value))
      : itemsToAdd.filter((item) => values.includes(item.value))
    if (!newItems) return

    const updatedItems = isAdded ? [...newItems] : [...itemsAdded, ...newItems]

    setSelectedItems([])
    setFocusedItem(null)
    onChange(updatedItems)
  }

  return (
    <div className="flex flex-col items-center gap-4 lg:flex-row lg:items-stretch">
      <div className="flex flex-col gap-2">
        {leftLabel && leftLabel}
        <List
          error={error}
          disabled={disabled}
          isLoading={isLoading}
          items={itemsToAdd.filter(
            (item) => !itemsAdded.find((i) => i.value === item.value),
          )}
          selectedItems={selectedItems}
          handleSelect={(value) => handleSelect(value, false)}
        />
      </div>

      <div className="flex flex-col justify-center gap-1">
        <Button
          type="button"
          aria-label="Transfer selected items to right"
          variant="accent"
          size="icon"
          onClick={() => handleTransfer(selectedItems, false)}
          disabled={disabled || isLoading}
        >
          <ChevronsRightIcon />
        </Button>

        <Button
          type="button"
          aria-label="Transfer selected items to left"
          variant="accent"
          size="icon"
          onClick={() => handleTransfer(selectedItems, true)}
          disabled={disabled || isLoading}
        >
          <ChevronsLeftIcon />
        </Button>
      </div>

      <div className="flex flex-col gap-2">
        {rightLabel && rightLabel}
        <List
          error={error}
          disabled={disabled}
          isLoading={isLoading}
          items={itemsAdded}
          selectedItems={selectedItems}
          handleSelect={(value) => handleSelect(value, true)}
        />
      </div>
    </div>
  )
}

TransferList.displayName = 'TransferList'
export { TransferList }
